import { useCallback, useState } from 'react';

import { GestureResponderEvent } from 'react-native';

import { toaster } from 'components/toaster/toaster';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import { HttpErrorCodes } from 'remote/constants';
import { useAuthContext } from 'state/auth';
import { getStoredOtpCredentials } from 'state/auth/hooks/use-account-authentication';
import { t } from 'state/intl/translations';

interface IUseSendNewOtpProps {
  setShowSendNewOtpForm: (showSendNewOtpForm: boolean) => void;
  setResendOrSendNewOtpError: (formError: string | null) => void;
  setOtpValidationError: (formError: string | null) => void;
  setOtpAuthError(error: null | string): void;
  setShowHasSentNewOtpMessage: (showHasSentNewOtpMessage: boolean) => void;
}

export const useSendNewOtp = ({
  setResendOrSendNewOtpError,
  setOtpValidationError,
  setOtpAuthError,
  setShowHasSentNewOtpMessage,
  setShowSendNewOtpForm,
}: IUseSendNewOtpProps) => {
  // use this `loadingState` rather than `loading` from `seAuthContext` so that I can fake the async request within storybook easily
  const [isLoading, setIsLoading] = useState(false);

  const { signIn } = useAuthContext();
  const { email } = getStoredOtpCredentials() || {};

  const sendNewOtp = useCallback(
    async (ev: GestureResponderEvent) => {
      ev.preventDefault();
      setShowHasSentNewOtpMessage(false);
      setIsLoading(true);
      setOtpValidationError(null);
      setOtpAuthError(null);

      try {
        await signIn({
          email,
        });

        setShowHasSentNewOtpMessage(true);
        setShowSendNewOtpForm(false);
        toaster.positive(t('resendCodeSuccess'));
      } catch (error) {
        switch (error?.graphQLErrors?.[0]?.extensions?.statusCode) {
          case HttpErrorCodes.TooManyRequests:
            setResendOrSendNewOtpError(
              logUserErrorMessage({
                message: `${t('maxAttemptsReached')} ${t('pleaseTryAgainLater')}`,
                severity: LogUserErrorSeverity.Irrecoverable,
              })
            );
            break;
          default:
            setResendOrSendNewOtpError(
              logUserErrorMessage({
                message: t('authError'),
                severity: LogUserErrorSeverity.Recoverable,
              })
            );
            break;
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      setShowHasSentNewOtpMessage,
      setOtpValidationError,
      setOtpAuthError,
      signIn,
      email,
      setShowSendNewOtpForm,
      setResendOrSendNewOtpError,
    ]
  );

  return {
    sendNewOtp,
    isLoading,
  };
};
